import styles from "./PatientPayment.module.scss";
import FormHeader from "components/atoms/FormHeader";
import Input from "../../atoms/Input";
import Button from "../../atoms/Button";
import { FC, Fragment, useEffect, useState, useRef } from "react";
import { sleep } from "../../../utils/general";
import recountAmountValue from "../../../utils/recountAmount";
import { raiseException } from "../../../utils/raiseException";
import { trpc } from "../../../utils/trpc";
import { analyticsService } from "../../../utils/analyticsService";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { envs } from "../../../utils/envs";
import { notification, Spin, Popconfirm, Select, Tooltip } from 'antd';
import { CloseOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import SavedCard from "../../molecules/SavedCard";
import { stripePromise } from "../../../utils/stripe";
import book from "../../../theme/images/book.webp"
import medal from "../../../theme/images/medal.webp"
import { format, differenceInMinutes } from "date-fns";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import priceWithDiscount from "../../../utils/priceWithDiscount";
import PatientPaymentForm from "../../PatientPaymentForm";
import { getNormalizedDate } from "../../../utils/dateTime";
import routes from "routes";
import { useTherapistData } from '../../../customHooks/therapistDataContext';
import { ArrowUturnLeftIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { checkInformedConsentStatus } from '../../../utils/informedConsentStatus'
import InformedConsentModal from '../../Modals/InformedConsentModal';
import Emitter from '../../../utils/eventemitter';

interface Props {
  setLoadingModalIsOpen?: any;
  isModal?: boolean;
  userInfo?: any;
  myTherapist?: any;
  closeModal?: any;
  savedPayment?: string;
  onPayAppointment?: () => void;
  paymentIntentId: string;
  clientSecret: string;
  setupFutureUsage?: boolean;
  amount?: any;
  appointment?: any;
  className?: any;
}

export const PatientPayment = ({
  setLoadingModalIsOpen,
  isModal,
  userInfo,
  myTherapist,
  closeModal,
  savedPayment,
  onPayAppointment,
  paymentIntentId,
  clientSecret,
  setupFutureUsage,
  amount,
  appointment,
  className
}: Props) => {
  const location = useLocation();
  const path = location.pathname
  const navigate = useNavigate();
  const {therapistData} = useTherapistData();
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [couponInfo, setCouponInfo] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [isToggle, setIsToggle] = useState(true);
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [genderedTitle, setGenderedTitle] = useState('');
  const [isModalWarningOpen, setIsModalWarningOpen] = useState(false);
  const [sessionPackage, setSessionPackage] = useState(true);
  const [_amount, setAmount] = useState<number | null>(null);
  const [informedConsentIsCompiled, setInformedConsentIsCompiled] =
    useState<null | boolean>(null);
  const [informedConsent, setInformedConsent] = useState<boolean>(false);

  const start = getNormalizedDate(appointment?.startTime);
  const end = getNormalizedDate(appointment?.endTime);
  const duration = differenceInMinutes(end, start);
  let isPaymentElementComplete = false;

  const startFormatted = start && format(start, 'HH:mm');
  const endFormatted = end && format(end, 'HH:mm');
  const _therapyType = therapistData?.therapyType || userInfo.therapyType

  const getPackage = trpc.mainService.packages.getPackageInfo.useQuery();

  const sessionPrice: any = !_amount && recountAmountValue(couponInfo, _therapyType, _amount || amount, !!appointment?.isFirstAppointment);
  const discountPercent: number = getPackage?.data?.package?.info?.discount_value || 0;
  const packagePrice: any = sessionPrice * 5;
  const discountedPrice: any = (packagePrice * (1 - discountPercent / 100)).toFixed(2);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    analyticsService({
      'event': 'virtualPageview',
      'pageUrl': path,
      'pageTitle': 'Patient Payment Methods'
    });
  }, [path]);

  const getBillingDetailsResponse =
    trpc.mainService.patient.getBillingDetails.useQuery(undefined, {
      enabled: isToggle
    })

  const allFieldsFilled = userInfo.role === 'patient' && areAllFieldsFilled(getBillingDetailsResponse?.data?.billingDetails);

  const getPersonalCouponInfo = trpc.mainService.coupons.getPersonalCouponInfo.useQuery({
    therapyType: _therapyType
  }, {enabled: isToggle});

  const {data: informedConsentResponse, isLoading: isInformedConsentLoading, refetch: refetchInformedConsent} =
    trpc.mainService.patient.getinformedConsent.useQuery({
      accountTherapyType: therapistData?.therapyType || undefined,
      additionalTherapistId: therapistData?.therapistId || undefined
    });

  const createCoupon = trpc.mainService.coupons.createCoupon.useMutation();
  const requiredInformedConsent = trpc.mainService.patient.getRequiredInformedConsent.useQuery();

  function areAllFieldsFilled(obj: any) {
    if (obj && Object.keys(obj).length > 0) {
      return Object.values(obj).every(value => Boolean(value));
    }
    return false;
  }

  const getMaxDiscountCoupon = (coupons: any, itemPrice: number, isFirstAppointment: boolean) => {
    const filteredCoupons = isFirstAppointment ? coupons.filter(coupon => !coupon.info.package) : coupons;
    if (filteredCoupons.length === 0) return null;

    const maxDiscountCoupon = filteredCoupons?.reduce((maxCoupon, currentCoupon) => {
      const calculateDiscountValue = (coupon: any) => {
        const {discount_type, discount_value} = coupon.info;

        if (discount_type === 'percentage') {
          return (discount_value / 100) * itemPrice;
        } else if (discount_type === 'flat') {
          return discount_value;
        }
        return 0;
      };

      const maxValue = calculateDiscountValue(maxCoupon);
      const currentValue = calculateDiscountValue(currentCoupon);

      return currentValue > maxValue ? currentCoupon : maxCoupon;
    });

    return maxDiscountCoupon as any;
  };

  useEffect(() => {
    const updateInformedConsentStatus = () => {
      if (isInformedConsentLoading) return;
      checkInformedConsentStatus(informedConsentResponse, setInformedConsentIsCompiled);
    };
    updateInformedConsentStatus();
  }, [informedConsentResponse, isInformedConsentLoading]);

  useEffect(() => {
    const updateInformedConsent = () => {
      if (isInformedConsentLoading) return;
      const user = requiredInformedConsent?.data?.user;
      if (user === null) {
        setInformedConsent(false);
        return;
      }
      if (informedConsentIsCompiled === null) {
        return;
      }
      setInformedConsent(!informedConsentIsCompiled);
    }
    if (isModal) {
      updateInformedConsent();
    }
  }, [isModal, informedConsentIsCompiled, requiredInformedConsent?.data?.user, isInformedConsentLoading]);


  useEffect(() => {
    Emitter.on('updateInformedConsent', () => {
      setInformedConsent(false)
      refetchInformedConsent()
    });
  }, []);

  useEffect(() => {
    if (isToggle) {
      if (!getBillingDetailsResponse?.isSuccess || !getPersonalCouponInfo?.isSuccess || !getPackage.isSuccess || !requiredInformedConsent.isSuccess || isInformedConsentLoading || informedConsentIsCompiled === null) {
        setLoadingToggle(true);
      } else if (
        getBillingDetailsResponse.isSuccess && getPersonalCouponInfo.isSuccess && getPackage.isSuccess && requiredInformedConsent.isSuccess && !isInformedConsentLoading
      ) {
        if (getPersonalCouponInfo?.data?.coupons?.some(coupon => coupon.info.package)) {
          setCouponInfo(getMaxDiscountCoupon(getPersonalCouponInfo?.data?.coupons, amount, !!appointment?.isFirstAppointment)?.info)
          setSessionPackage(false);
        }
        if (appointment?.isFirstAppointment || !getPackage?.data?.package.active || discountPercent === 0) {
          setSessionPackage(false);
        }
        setLoadingToggle(false);
        setIsToggle(false);
        if (myTherapist) {
          if (myTherapist.therapyType?.includes('psychiatry')) {
            if (myTherapist.gender === 'male') {
              setGenderedTitle('il dr.');
            } else {
              setGenderedTitle('la dr.ssa');
            }
          } else {
            if (myTherapist.gender === 'male') {
              setGenderedTitle('il terapeuta');
            } else {
              setGenderedTitle('la terapeuta');
            }
          }
        }
      }
    }
  }, [
    isToggle,
    loadingToggle,
    getBillingDetailsResponse.isSuccess, getPersonalCouponInfo.isSuccess, getPackage.isSuccess, requiredInformedConsent.isSuccess, isInformedConsentLoading, informedConsentIsCompiled
  ]);

  useEffect(() => {
    if (!getPersonalCouponInfo?.data?.coupons?.some(coupon => coupon.info.package) && getPersonalCouponInfo?.data?.coupons?.some(coupon => coupon.info.status === 'active') && !sessionPackage && !_amount) {
      setCouponInfo(getMaxDiscountCoupon(getPersonalCouponInfo?.data?.coupons, amount, !!appointment?.isFirstAppointment).info)
    }
  }, [sessionPackage, _amount]);

  const InformBlock: any = () => {

    return (
      <div className={styles.infoBlockWrapper}>
        <div className={styles.bookWrapper}>
          <img src={book} alt="book"/>
        </div>
        <div className={styles.infoBlockDateWrapper}>
          <div className={styles.infoBlockDate}>{getNormalizedDate(appointment?.startTime).toLocaleDateString('it-IT', {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          </div>
          <div>
            {startFormatted} &gt; {endFormatted} ({duration} minuti)
          </div>
        </div>
      </div>
    )
  }

  interface CheckoutCompProps {
    paymentIntentId: string;
    clientSecret: string;
    onPayAppointment?: () => void;
    savedPayment?: string;
    setupFutureUsage?: boolean;
    checkoutAmount: number;
    appointmentId: string;
    isPaymentLoading: boolean;
    myTherapist: any;
    setIsPaymentLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setPaymentStatus: React.Dispatch<
      React.SetStateAction<"idle" | "success" | "error">
    >;
  }

  const CheckoutComp: FC<CheckoutCompProps> = ({
    paymentIntentId,
    clientSecret,
    onPayAppointment,
    savedPayment,
    setupFutureUsage: setupFutureUsageDefault,
    checkoutAmount,
    appointmentId,
    isPaymentLoading,
    setIsPaymentLoading,
    setPaymentStatus,
    myTherapist
  }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [name, setName] = useState("");
    const [useSavedCard, setUseSavedCard] = useState(Boolean(savedPayment));
    const [setupFutureUsage, setSetupFutureUsage] = useState(
      setupFutureUsageDefault
    );
    const [selectedSavedCard, setSelectedSavedCard] = useState("");
    const [couponValue, setCouponValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputWidth, setInputWidth] = useState('300px');
    const [isStripeLoading, setIsStripeLoading] = useState(false);
    const [api, contextHolder] = notification.useNotification();

    const getAppointment = trpc.mainService.appointment.getAppointment.useQuery({
      appointmentId,
    });

    const updatePaymentIntent =
      trpc.mainService.payments.stripe.updatePaymentIntent.useMutation();

    const listPaymentMethodsResponse =
      trpc.mainService.payments.stripe.listPaymentMethods.useQuery();

    const getCouponInfo = trpc.mainService.coupons.getCoupon.useMutation();

    const setCouponUsage = trpc.mainService.coupons.couponUsage.useMutation();

    const paidAppointmentWithFullDiscount = trpc.mainService.coupons.freeCouponAppointment.useMutation();

    const transferAfterCouponUsage = trpc.mainService.coupons.transferAfterCouponUsage.useMutation();

    const updatePaymentIntentAfterCouponUsage = trpc.mainService.coupons.updatePaymentIntentAfterCouponUsage.useMutation();

    const sendBuyPackageEmail = trpc.mainService.packages.sendBuyPackageEmail.useMutation();
    const sendPackageLeftSessionsEmail = trpc.mainService.packages.sendPackageLeftSessionsEmail.useMutation();

    const getPlaceholderText = () => {
      return couponInfo
        ? `Hai applicato uno sconto ${couponInfo?.discount_type === 'flat' ? 'di' : 'del'} ${couponInfo?.discount_value} ${getDiscountText(couponInfo)}`
        : 'Utilizza un CODICE SCONTO';
    };

    const sharedButtonStyles = {
      backgroundColor: '#9ebbff',
      fontFamily: 'Poppins',
      borderRadius: '1.375rem',
      padding: '.25rem 1rem',
      boxShadow: 'none',
      color: '#2c2c4b',
      border: '.125rem solid #9ebbff',
    };

    useEffect(() => {
      const placeholderText = getPlaceholderText();
      const text = inputRef?.current?.value || placeholderText;
      const dummyDiv = document.createElement('div');
      dummyDiv.style.position = 'absolute';
      dummyDiv.style.visibility = 'hidden';
      dummyDiv.style.whiteSpace = 'nowrap';
      dummyDiv.textContent = text;
      document.body.appendChild(dummyDiv);
      const formContentElement = document.querySelector(`.${styles.formContent}`);
      const formContentWidth = formContentElement
        ? formContentElement.getBoundingClientRect().width
        : 500;
      const width = Math.min(dummyDiv.offsetWidth, formContentWidth - 32);
      document.body.removeChild(dummyDiv);
      setInputWidth(`${Math.max(300, width)}px`);
    }, [couponInfo, couponValue]);

    const openNotification = (variant: string, text: string) => {
      api.open({
        message: null,
        description: (
          <span style={{fontWeight: 400, fontSize: '0.875rem'}}>
          {text}
        </span>
        ),
        className: `info ${variant}`,
        duration: 3,
        icon: <InfoCircleOutlined/>,
        closeIcon: <CloseOutlined/>,
      });
    };

    const handleCouponInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCouponValue(e.target.value);
    };

    const verifyCoupon = async () => {
      try {
        const { coupon }: any = await getCouponInfo.mutateAsync({
          coupon: couponValue,
          therapyType: userInfo?.therapyType
        });

        if (coupon?.id) {
          setCouponInfo(coupon);
          openNotification('confirm', `Hai applicato uno sconto su ${coupon?.discount_value} ${getDiscountText(coupon)}`)
        }
      } catch (error: any) {
        raiseException(error);
        if (error?.shape?.errorCode === "CouponNotFound") {
          openNotification('error', 'Questo coupon non esiste, controlla se il coupon è corretto e riprova.')
        } else if (error?.shape?.errorCode === "CouponInactiveStatus") {
          openNotification('error', 'Sembra che il coupon non sia più attivo.')
        } else if (error?.shape?.errorCode === "ExpiredCoupon") {
          openNotification('error', 'Il coupon è scaduto.')
        } else if (error?.shape?.errorCode === "CouponNotActiveYet") {
          openNotification('error', error?.shape?.message)
        } else if (error?.shape?.errorCode === "LimitExceeded") {
          openNotification('error', `Il limite per l'utilizzo di questo coupon è stato superato.`)
        } else if (error?.shape?.errorCode === "LimitExceededByUser") {
          openNotification('error', 'Codice coupon non valido per il tipo di terapia.')
        } else if (error?.shape?.errorCode === "CouponTherapyTypeMismatch") {
          openNotification('error', 'Il tipo di terapia non corrisponde ai tipi di terapia di questo coupon.')
        }
      }
    };

    useEffect(() => {
      if (!selectedSavedCard) {
        if (userInfo?.stripeDefaultPaymentMethodId) {
          const cardExists = listPaymentMethodsResponse.data
            ? listPaymentMethodsResponse.data.find(
              (card) =>
                card.id ===
                userInfo?.stripeDefaultPaymentMethodId
            )
            : false;
          if (cardExists) {
            setSelectedSavedCard(
              userInfo?.stripeDefaultPaymentMethodId
            );
          }
        } else if (
          listPaymentMethodsResponse.data &&
          listPaymentMethodsResponse.data?.length > 0
        ) {
          setSelectedSavedCard(listPaymentMethodsResponse.data[0].id);
        }
      }

    }, [listPaymentMethodsResponse.data, userInfo]);

    const payForAppointment = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsStripeLoading(true);
      if (stripe === null || elements === null) {
        setIsStripeLoading(false);
        setPaymentStatus("error");
        return;
      }

      if (!useSavedCard) {
        const {error: submitError} = await elements.submit();
        if (submitError) {
          setIsStripeLoading(false);
        }

        if (!isPaymentElementComplete) {
          openNotification('error', 'Non sono stati compilati tutti i dati richiesti')
          if (modalRef.current) {
            modalRef.current.scrollTop = modalRef.current.scrollHeight;
          }
          return;
        }
      }

      const {
        patientPayValue,
        transferToTherapistValue,
        additionalTransferValue,
        transferAmount
      } = await countAdditionalTransferValue();
      await updateTransfer(true, patientPayValue, transferToTherapistValue);

      const {error}: any = await stripe.confirmPayment({
        clientSecret,
        ...(!useSavedCard ? {elements} : {}),
        confirmParams: {
          ...(useSavedCard
            ? {
              payment_method: selectedSavedCard,
            }
            : {}),
          return_url: `${envs.REACT_APP_FRONTEND_URL}/dashboard/chat`,
        },
        redirect: "if_required",
      });
      if (error) {
        await updateTransfer(true, amount * 100, transferToTherapistValue + additionalTransferValue);
        console.log(error);
        setIsStripeLoading(false);
        setPaymentStatus('error');
      } else {
        setIsPaymentLoading(true);
        let loading = true;
        if (additionalTransferValue > 0) {
          await transferToTherapist(additionalTransferValue, transferAmount);
        }
        while (loading) {
          await sleep(1000);
          const result = await getAppointment.refetch();
          if (result.data?.appointment.status === 'ready' || result.data?.appointment.status === 'payed_to_be_confirmed') {
            analyticsService({
              'event': 'purchase',
              'conversion_value': `${recountAmountValue(couponInfo, _therapyType, checkoutAmount, !!result.data?.appointment?.isFirstAppointment)} €`
            });
            loading = false;
            setPaymentStatus("success");
            setIsPaymentLoading(false);
            if (onPayAppointment) {
              onPayAppointment();
            }
            if (_amount) {
              let couponId: any = null
              await createCoupon.mutateAsync({
                numberOfUsages: 5,
                therapyType: _therapyType
              }).then(async (res) => {
                couponId = res.coupon.id
                await getPersonalCouponInfo.refetch().then(async (res) => {
                  const data: any = res?.data?.coupons.find((coupon: any) => coupon.info.id === couponId)
                  data.info.package && await sendPackageLeftSessionsEmail.mutateAsync({
                    sessionNumber: data.info.couponUsages + 1,
                    totalSessionNumber: data.info.max_usage_total,
                    leftSessionNumber: data.info.numberOfUsagesLeft - 1,
                    therapyType: _therapyType
                  })
                })
                await getPackage.refetch()
                await sendBuyPackageEmail.mutateAsync()
                setCouponInfo(res.coupon)
                await couponUsage(res.coupon)
                setAmount(null)
                setIsToggle(true)
              })
            } else {
              await couponUsage();
            }
          }
        }
      }
    };

    const makePaidAppointmentFree = async () => {
      let loading = true;

      try {
        setIsPaymentLoading(true);

        const { patientPayValue, transferToTherapistValue, additionalTransferValue, transferAmount} = await countAdditionalTransferValue();

        await updateTransfer(false, patientPayValue, transferToTherapistValue);

        await paidAppointmentWithFullDiscount.mutateAsync({
          appointmentId,
          stripeExpressAccountId: myTherapist?.stripeExpressAccountId,
          transferAmount: additionalTransferValue!,
          fullPrice: transferAmount
        });

        await couponUsage();

        while (loading) {
          await sleep(1000);
          const result = await getAppointment.refetch();
          if (result.data?.appointment.status === 'ready' || result.data?.appointment.status === 'payed_to_be_confirmed') {
            loading = false;
            couponInfo.package && await sendPackageLeftSessionsEmail.mutateAsync({
              sessionNumber: couponInfo.couponUsages + 1,
              totalSessionNumber: couponInfo.max_usage_total,
              leftSessionNumber: couponInfo.numberOfUsagesLeft - 1,
              therapyType: _therapyType
            })
            setPaymentStatus("success");
            setIsPaymentLoading(false);
            setIsToggle(true)
            if (onPayAppointment) {
              onPayAppointment();
            }
          }
        }
      } catch (error: any) {
        loading = false
        if (error?.shape?.errorCode === "CantMakeTransfer") {
          openNotification('error', 'Si è verificato un errore, riprova più tardi.')
        }
        setIsStripeLoading(false);
        setPaymentStatus('error');
        raiseException(error);
      }
    }

    const countAdditionalTransferValue = async (value?: number) => {
      const therapyType = userInfo?.therapyType;
      const result = await getAppointment.refetch();
      const amount = value ? value : checkoutAmount

      const { patientPayValue, transferToTherapistValue, additionalTransferToTherapist, transferAmount } = await priceWithDiscount(
        (recountAmountValue(couponInfo, _therapyType, amount, !!result.data?.appointment?.isFirstAppointment) * 100),
        therapyType,
        myTherapist?.vatNumberType,
        result.data?.appointment?.isFirstAppointment
      );

      return {
        patientPayValue,
        transferToTherapistValue,
        additionalTransferValue: additionalTransferToTherapist,
        transferAmount
      };
    }

    const updateTransfer = async (updateTransfer: boolean, patientPayValue: number, transferToTherapistValue: number) => {
      try {
        if (updateTransfer) {
          await updatePaymentIntentAfterCouponUsage.mutateAsync({
            paymentAmount: patientPayValue,
            transferAmount: transferToTherapistValue,
            paymentIntentId,
          })
        }
      } catch (error) {
        raiseException(error);
      }
    }

    const couponUsage = async (data?: any) => {
      try {
        if (couponInfo?.id || data?.id) {
          await setCouponUsage.mutateAsync({
            couponId: couponInfo?.id || data?.id,
            appointmentId,
          });
        }
      } catch (error) {
        raiseException(error);
      }
    }

    const transferToTherapist = async (additionalTransferValue: number, finalAmount: number) => {
      if (couponInfo?.id) {
        await transferAfterCouponUsage.mutateAsync({
          stripeExpressAccountId: myTherapist?.stripeExpressAccountId,
          additionalTransferToTherapist: additionalTransferValue,
          fullPrice: finalAmount
        })
      }
    }

    const getDiscountText = (coupon: any) => {
      return coupon.discount_type === 'percentage' ? '%' : '€';
    };

    const paymentElementsChange = (event: any) => {
      isPaymentElementComplete = event?.complete;
    };

    const activeCoupons: any = getPersonalCouponInfo?.data?.coupons.filter(coupon => {
      if (!!appointment?.isFirstAppointment) {
        return coupon.info.status === 'active' && !coupon.info.package;
      } else {
        return coupon.info.status === 'active';
      }
    });

    const options = activeCoupons?.map(coupon => {
      const {coupon: couponCode, discount_value, from_date, to_date} = coupon.info;

      const shortFromDate = new Date(from_date).toLocaleDateString();
      const shortToDate = new Date(to_date).toLocaleDateString();

      const _couponInfo = `Codice sconto - ${discount_value} ${getDiscountText(coupon.info)}`;

      return {
        value: couponCode,
        label: (
          <>
            <span>{_couponInfo}</span>
            <Tooltip zIndex={20001}
                     title={
                       <>
                         Valido dal {shortFromDate} al {shortToDate} <br/>
                         {coupon.info?.package
                           ? `Utilizzi: ${coupon.info.couponUsages}/${coupon.info.max_usage_total}`
                           : ''}
                       </>}>
              <InfoCircleOutlined style={{marginLeft: 8}}/>
            </Tooltip>
          </>
        ),
      };
    });

    const handleCouponSelect = (value) => {
      const coupon = activeCoupons?.find(coupon => coupon.info.coupon === value);
      setCouponInfo(coupon.info);
      setSessionPackage(false);
    };


    return (
      <div className={styles.formContent}>
        {(() => {
          if (isPaymentLoading) {
            return (
              <Spin
                className={styles.spinner}
                indicator={<LoadingOutlined style={{fontSize: 32}} spin/>}
              />
            );
          } else {
            return (
              <Fragment>
                {contextHolder}
                {(activeCoupons.length >= 1) && !couponInfo && !_amount &&
                  <Select
                  popupClassName="couponSelect"
                  className="couponSelect"
                  placeholder="Scegliere un buono sconto"
                  options={options}
                  onChange={handleCouponSelect}
                />}
                {!_amount && <div className={styles.couponWrapper}>
                  <div className={styles.inputWrapper}>
                    {couponInfo && activeCoupons.length >= 1 &&
                      <Popconfirm
                        title="Аnnullamento del coupon"
                        zIndex={20000}
                        okButtonProps={{style: sharedButtonStyles}}
                        cancelButtonProps={{
                          style: {...sharedButtonStyles, backgroundColor: 'transparent'}
                        }}
                        description="È sicuro di voler annullare il coupon?"
                        onConfirm={() => setCouponInfo(null)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <div className={styles.couponClose}><XMarkIcon/></div>
                      </Popconfirm>}
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder={getPlaceholderText()}
                    coupon
                    disabled={!!couponInfo?.id}
                    value={couponValue}
                    onChange={handleCouponInputChange}
                    width={inputWidth}
                  />
                    {couponInfo?.package && activeCoupons?.length >= 1 &&
                      <div
                        className={styles.couponSession}>{`Utilizzi: ${couponInfo.couponUsages}/${couponInfo.max_usage_total}`}</div>}
                  </div>
                  {couponValue.length >= 5 && (
                    <Button className="small" onClick={verifyCoupon}>
                      Verificare
                    </Button>
                  )}
                </div>}
                {useSavedCard ? (
                  listPaymentMethodsResponse.data
                    ?.sort((paymentMethodA, paymentMethodB) => {
                      if (
                        paymentMethodA.id ===
                        userInfo?.stripeDefaultPaymentMethodId
                      ) {
                        return -1;
                      }
                      if (
                        paymentMethodB.id ===
                        userInfo?.stripeDefaultPaymentMethodId
                      ) {
                        return 1;
                      }
                      return 0;
                    })
                    .map((paymentMethod) => (
                      <SavedCard
                        key={paymentMethod.id}
                        id={paymentMethod.id}
                        last4={paymentMethod.card?.last4!}
                        brand={paymentMethod.card?.brand!}
                        isDefault={
                          paymentMethod.id ===
                          userInfo?.stripeDefaultPaymentMethodId
                        }
                        isSelected={paymentMethod.id === selectedSavedCard}
                        isSelectable={true}
                        onClick={() => {
                          setSelectedSavedCard(paymentMethod.id);
                        }}
                        showDefaultLabel={true}
                        hideDelete={true}
                        hideSetDefault={true}
                        onChange={() => {
                          listPaymentMethodsResponse.refetch();
                        }}
                      />
                    ))) : (
                  // <div className={styles.defaultCard}>
                  //   <div>
                  //     <svg
                  //       width="44"
                  //       height="44"
                  //       viewBox="0 0 44 44"
                  //       fill="none"
                  //       xmlns="http://www.w3.org/2000/svg"
                  //     >
                  //       <rect width="44" height="44" rx="22" fill="#DFEAFF" />
                  //       <rect
                  //         x="10.207"
                  //         y="13.9209"
                  //         width="23.586"
                  //         height="16.1574"
                  //         rx="1.44905"
                  //         fill="white"
                  //         stroke="#F3F3F3"
                  //         strokeWidth="0.414014"
                  //       />
                  //       <path
                  //         fillRule="evenodd"
                  //         clipRule="evenodd"
                  //         d="M18.7918 26.5121V27.124V27.7358H18.524V27.5873C18.439 27.6974 18.3101 27.7666 18.1349 27.7666C17.7895 27.7666 17.5187 27.4979 17.5187 27.124C17.5187 26.7504 17.7895 26.4814 18.1349 26.4814C18.3101 26.4814 18.439 26.5505 18.524 26.6607V26.5121H18.7918ZM18.1682 26.7324C17.9363 26.7324 17.7944 26.9091 17.7944 27.1241C17.7944 27.3392 17.9363 27.5159 18.1682 27.5159C18.3897 27.5159 18.5393 27.3469 18.5393 27.1241C18.5393 26.9014 18.3897 26.7324 18.1682 26.7324ZM27.8432 27.1241C27.8432 26.9091 27.985 26.7324 28.2169 26.7324C28.4387 26.7324 28.588 26.9014 28.588 27.1241C28.588 27.3469 28.4387 27.5159 28.2169 27.5159C27.985 27.5159 27.8432 27.3392 27.8432 27.1241ZM28.8409 26.0207V27.124V27.7358H28.5727V27.5873C28.4878 27.6974 28.3589 27.7666 28.1836 27.7666C27.8382 27.7666 27.5674 27.4979 27.5674 27.124C27.5674 26.7504 27.8382 26.4814 28.1836 26.4814C28.3589 26.4814 28.4878 26.5505 28.5727 26.6607V26.0207H28.8409ZM22.1164 26.7198C22.289 26.7198 22.3999 26.8273 22.4282 27.0166H21.789C21.8176 26.8399 21.9256 26.7198 22.1164 26.7198ZM21.5084 27.124C21.5084 26.7425 21.7609 26.4814 22.122 26.4814C22.4671 26.4814 22.7043 26.7425 22.7069 27.124C22.7069 27.1597 22.7043 27.1931 22.7016 27.2263H21.7869C21.8255 27.4466 21.9827 27.526 22.1553 27.526C22.2789 27.526 22.4105 27.4797 22.5137 27.398L22.645 27.5952C22.4954 27.7206 22.3255 27.7666 22.14 27.7666C21.7712 27.7666 21.5084 27.5131 21.5084 27.124ZM25.3562 27.1241C25.3562 26.9091 25.4981 26.7324 25.73 26.7324C25.9515 26.7324 26.1011 26.9014 26.1011 27.1241C26.1011 27.3469 25.9515 27.5159 25.73 27.5159C25.4981 27.5159 25.3562 27.3392 25.3562 27.1241ZM26.3532 26.5121V27.124V27.7358H26.0854V27.5873C26.0002 27.6974 25.8715 27.7666 25.6963 27.7666C25.3509 27.7666 25.0801 27.4979 25.0801 27.124C25.0801 26.7504 25.3509 26.4814 25.6963 26.4814C25.8715 26.4814 26.0002 26.5505 26.0854 26.6607V26.5121H26.3532ZM23.8433 27.124C23.8433 27.4953 24.1035 27.7666 24.5005 27.7666C24.6861 27.7666 24.8097 27.7256 24.9436 27.6207L24.815 27.4056C24.7144 27.4774 24.6088 27.5158 24.4926 27.5158C24.2787 27.5131 24.1215 27.3596 24.1215 27.124C24.1215 26.8884 24.2787 26.7348 24.4926 26.7322C24.6088 26.7322 24.7144 26.7706 24.815 26.8424L24.9436 26.6273C24.8097 26.5224 24.6861 26.4814 24.5005 26.4814C24.1035 26.4814 23.8433 26.7527 23.8433 27.124ZM26.9721 26.6607C27.0417 26.5532 27.1423 26.4814 27.2969 26.4814C27.3512 26.4814 27.4285 26.4917 27.4878 26.5148L27.4052 26.7657C27.3485 26.7425 27.2919 26.7349 27.2376 26.7349C27.0624 26.7349 26.9748 26.8474 26.9748 27.0499V27.7359H26.7066V26.5122H26.9721V26.6607ZM20.1165 26.6094C19.9876 26.525 19.81 26.4814 19.6142 26.4814C19.3021 26.4814 19.1012 26.6299 19.1012 26.8732C19.1012 27.0727 19.2508 27.1958 19.5263 27.2342L19.6528 27.252C19.7997 27.2726 19.869 27.3109 19.869 27.3801C19.869 27.4747 19.7711 27.5287 19.5882 27.5287C19.4027 27.5287 19.2687 27.4698 19.1785 27.4006L19.0522 27.6078C19.1991 27.7153 19.3847 27.7666 19.5856 27.7666C19.9413 27.7666 20.1475 27.6002 20.1475 27.3672C20.1475 27.1521 19.9853 27.0396 19.7171 27.0012L19.5909 26.983C19.4749 26.9678 19.382 26.9449 19.382 26.8629C19.382 26.7732 19.4696 26.7196 19.6165 26.7196C19.7738 26.7196 19.926 26.7785 20.0006 26.8245L20.1165 26.6094ZM23.248 26.6607C23.3173 26.5532 23.4179 26.4814 23.5725 26.4814C23.6268 26.4814 23.7041 26.4917 23.7634 26.5148L23.6808 26.7657C23.6241 26.7425 23.5675 26.7349 23.5132 26.7349C23.338 26.7349 23.2504 26.8474 23.2504 27.0499V27.7359H22.9825V26.5122H23.248V26.6607ZM21.2869 26.5121H20.8489V26.1409H20.5781V26.5121H20.3282V26.7554H20.5781V27.3136C20.5781 27.5975 20.689 27.7666 21.0058 27.7666C21.122 27.7666 21.256 27.7309 21.3409 27.672L21.2636 27.444C21.1837 27.49 21.0961 27.5131 21.0265 27.5131C20.8925 27.5131 20.8489 27.4311 20.8489 27.3083V26.7554H21.2869V26.5121ZM17.2818 26.9678V27.7359H17.011V27.0549C17.011 26.8474 16.9234 26.7323 16.7405 26.7323C16.5626 26.7323 16.439 26.8451 16.439 27.0575V27.7359H16.1682V27.0549C16.1682 26.8474 16.0783 26.7323 15.9004 26.7323C15.7172 26.7323 15.5986 26.8451 15.5986 27.0575V27.7359H15.3281V26.5122H15.5963V26.6631C15.6969 26.5198 15.8255 26.4814 15.957 26.4814C16.1452 26.4814 16.2791 26.5635 16.3641 26.6991C16.4777 26.5274 16.6399 26.4788 16.7971 26.4814C17.0963 26.4841 17.2818 26.6786 17.2818 26.9678Z"
                  //         fill="#231F20"
                  //       />
                  //       <path
                  //         d="M24.0879 24.4327H20.0283V17.1855H24.0879V24.4327Z"
                  //         fill="#FF5F00"
                  //       />
                  //       <path
                  //         d="M20.2862 20.8089C20.2862 19.3388 20.9791 18.0292 22.0582 17.1853C21.2691 16.5682 20.2732 16.1999 19.191 16.1999C16.6287 16.1999 14.5518 18.2634 14.5518 20.8089C14.5518 23.3545 16.6287 25.418 19.191 25.418C20.2732 25.418 21.2691 25.0496 22.0582 24.4325C20.9791 23.5886 20.2862 22.279 20.2862 20.8089Z"
                  //         fill="#EB001B"
                  //       />
                  //       <path
                  //         d="M29.5653 20.8089C29.5653 23.3545 27.4883 25.418 24.9261 25.418C23.8438 25.418 22.848 25.0496 22.0586 24.4325C23.1379 23.5886 23.8309 22.279 23.8309 20.8089C23.8309 19.3388 23.1379 18.0292 22.0586 17.1853C22.848 16.5682 23.8438 16.1999 24.9261 16.1999C27.4883 16.1999 29.5653 18.2634 29.5653 20.8089Z"
                  //         fill="#F79E1B"
                  //       />
                  //     </svg>
                  //   </div>
                  //   <div className={styles.defaultCardInfo}>
                  //     <div className={styles.cardBrand}>
                  //       {capitalize(
                  //         getPaymentMethodResponse.data?.card?.brand ?? ""
                  //       )}
                  //     </div>
                  //     <div className={styles.cardLast4}>
                  //       {getPaymentMethodResponse.data?.card?.last4}
                  //     </div>
                  //   </div>
                  // </div>
                  <>
                    {recountAmountValue(couponInfo, _therapyType, checkoutAmount, !!appointment?.isFirstAppointment) > 0 && (
                      <>
                        <div className={styles.nameFieldBottomSpace}>
                          <Input
                            type="text"
                            label="Intestatario"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                          />
                        </div>
                        {!isToggle && (
                          <PaymentElement
                            options={{
                              layout: {
                                type: "accordion",
                                defaultCollapsed: false,
                                radios: false,
                                spacedAccordionItems: true,
                              },
                              defaultValues: {
                                billingDetails: {
                                  name,
                                  email: userInfo?.email,
                                },
                              },
                            }}
                            onChange={paymentElementsChange}
                          />
                        )}
                        <div className={styles.futurePayments}>
                          <input
                            type="checkbox"
                            checked={setupFutureUsage}
                            onChange={(e) => {
                              setSetupFutureUsage(e.target.checked);
                              updatePaymentIntent.mutateAsync({
                                paymentIntentId,
                                setupFutureUsage: e.target.checked,
                              });
                            }}
                          ></input>
                          <label>salva carta per usi futuri</label>
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className={styles.formButtons}>
                  {savedPayment && (
                    <Button
                      onClick={() => {
                        setUseSavedCard(!useSavedCard);
                      }}
                      variant="tertiary"
                    >
                      {useSavedCard
                        ? "Usa un altro metodo"
                        : "Usa carta predefinita"}
                    </Button>
                  )}
                  {recountAmountValue(couponInfo, _therapyType, checkoutAmount, !!appointment?.isFirstAppointment) > 0 ? (
                    <Button
                      disabled={
                        (!useSavedCard && !name?.trim()) ||
                        (useSavedCard && !selectedSavedCard) ||
                        isStripeLoading
                      }
                      onClick={payForAppointment}
                    >
                      Paga {recountAmountValue(couponInfo, _therapyType, checkoutAmount, !!appointment?.isFirstAppointment)} €
                    </Button>
                  ) : (
                    <Button onClick={makePaidAppointmentFree}>
                      Accetta
                    </Button>
                  )}
                </div>
              </Fragment>
            );
          }
        })()}
      </div>
    );
  };

  return (
    <>
      {isToggle &&
        <Spin indicator={<LoadingOutlined style={{fontSize: 32}} spin/>}/>}
      {!isToggle && informedConsent &&
        <InformedConsentModal
          userTherapy={therapistData?.therapyType}
          therapist={myTherapist}
          therapistData={therapistData}
          setInformedConsentModalIsOpen={closeModal}
          userInfo={userInfo}
          informedConsentResponse={informedConsentResponse}
          setSignatureModal={false}
          page="payment"
          informedConsent={requiredInformedConsent?.data?.user}
        />}
      {!isToggle && !informedConsent &&
        <div ref={modalRef}
             className={`${styles.patientPayment} ${isModal ? styles.widget : ''}  ${isModal && paymentStatus === 'success' && styles.success} ${className}`}
             onClick={e => {
               isModal && e.stopPropagation()
             }}>

          {!isModalWarningOpen &&
            (isModal ? <FormHeader
              title={(() => {
                if (paymentStatus !== 'success') {
                  if (paymentStatus === 'error') {
                    return 'Errore';
                  } else if (isPaymentLoading || isToggle) {
                    return '';
                  } else if (sessionPackage) {
                    return 'Scegli le modalità di pagamento';
                  } else {
                    return 'Conferma il tuo appuntamento';
                  }
                }
              })()}
              closeModal={paymentStatus === 'idle' ? () => setIsModalWarningOpen(true) : closeModal}
              closeButton
              className={styles.modalHeader}
              classNameCloseButton={styles.classNameCloseButton}
            /> : <h2 className={styles.subtitle}>Dati di fatturazione</h2>)}
          {<>
            {!isModal &&
              <PatientPaymentForm
                getBillingDetailsResponse={getBillingDetailsResponse}
                isModal={isModal}
                userInfo={userInfo}
                setLoadingModalIsOpen={setLoadingModalIsOpen}
                closeModal={closeModal}
              />}
            {isModalWarningOpen &&
              <>
                <FormHeader
                  title=""
                  closeModal={closeModal}
                  closeButton
                />
                <div className={styles.warningModalContent}>
                  Ricordati di effettuare il pagamento della seduta prima dell'inizio della stessa, altrimenti la
                  prenotazione
                  sarà annullata.
                </div>
                <Button
                  leftIcon={<ArrowUturnLeftIcon/>}
                  className="small"
                  variant="secondary"
                  onClick={async () => {
                    closeModal();
                    navigate(routes.Chat.path);
                  }}
                >
                  Vai alla chat
                </Button>
              </>
            }
            {
              (paymentStatus === 'idle' && isModal && !isModalWarningOpen) && sessionPackage &&
              <>
                <div className={styles.packageContainer}>
                  <div className={styles.card} onClick={() => {
                    setAmount(discountedPrice)
                    setSessionPackage(false)
                  }}>
                    <div className={styles.packageContainerTitle}>Compra un pacchetto di 5 sedute</div>
                    <p className={styles.description}>Risparmia il {discountPercent}% su ogni seduta</p>
                    <div className={styles.priceContainer}>
                      <span className={styles.oldPrice}>{packagePrice} €</span>
                      <span className={styles.newPrice}>{discountedPrice} €</span>
                    </div>
                    <Button className={`${styles.button} small`}>Acquista il pacchetto</Button>
                  </div>
                  <div className={styles.card} onClick={() => setSessionPackage(false)}>
                    <div className={styles.packageContainerTitle}>Compra una singola seduta</div>
                    <p
                      className={styles.price}>{sessionPrice} €</p>
                    <Button className={`${styles.button} small`}>Acquista la seduta</Button>
                  </div>
                </div>
              </>
            }
            {(paymentStatus === 'idle' && isModal && !isModalWarningOpen) && !sessionPackage &&
              <>
                <div
                  className={styles.description}>{`Il tuo appuntamento con ${genderedTitle}  ${myTherapist.given_name} ${myTherapist.family_name} è quasi pronto! Se hai
          bisogno di apportare modifiche
          all’orario o alla data, puoi contattare direttamente ${genderedTitle} ${myTherapist.given_name} ${myTherapist.family_name} attraverso la chat e
          pianificarne uno nuovo!`}
                </div>
                <InformBlock/>
                {
                  isToggle && (
                    <Spin
                      className={styles.spinner}
                      indicator={<LoadingOutlined style={{fontSize: 32}} spin/>}
                    />
                  )
                }
                {!isToggle &&
                  (!allFieldsFilled ?
                      <PatientPaymentForm
                        getBillingDetailsResponse={getBillingDetailsResponse}
                        isModal={isModal}
                        userInfo={userInfo}
                        setLoadingModalIsOpen={setLoadingModalIsOpen}
                        closeModal={closeModal}
                      />
                      :
                      <div className={styles.form}>
                        <Elements
                          stripe={stripePromise}
                          options={{
                            locale: 'it-IT',
                            clientSecret: clientSecret,
                            appearance: {
                              theme: 'stripe',
                              rules: {
                                '.Input': {
                                  border: '0.0625rem solid #dfeaff',
                                },
                                '.AccordionItem': {
                                  border: '0.0625rem solid #dfeaff',
                                },
                              },
                            },
                          }}
                        >
                          <CheckoutComp
                            paymentIntentId={paymentIntentId}
                            clientSecret={clientSecret}
                            onPayAppointment={onPayAppointment}
                            savedPayment={savedPayment}
                            setupFutureUsage={setupFutureUsage}
                            checkoutAmount={_amount! || amount!}
                            appointmentId={appointment.id}
                            isPaymentLoading={isPaymentLoading}
                            setIsPaymentLoading={setIsPaymentLoading}
                            setPaymentStatus={setPaymentStatus}
                            myTherapist={myTherapist}
                          />
                        </Elements>
                      </div>
                  )
                }
              </>
            }
            {(paymentStatus === 'success' && isModal) &&
              <div className={styles.successPaymentWrapper}>
                <div className={styles.imgWrapper}>
                  <img src={medal} alt="medal"/>
                </div>
                <p className={styles.title}>
                  {userInfo?.given_name} è tutto Pronto!
                </p>
                <p className={styles.text}>
                  Il pagamento per la seduta con {genderedTitle}&nbsp;
                  <span>{myTherapist.given_name}</span>{' '}<span>{myTherapist.family_name}</span> è stato completato
                  con
                  successo.
                </p>
                <InformBlock/>
                <Button rightIcon={<ChevronRightIcon/>} onClick={async () => {
                  location.pathname === routes.PatientBookingFirstAppointment.path && navigate(routes.Chat.path);
                  closeModal();
                }}>
                  {location.pathname === routes.PatientBookingFirstAppointment.path ? 'Vai alla chat' : 'Torna alla chat'}
                </Button>
              </div>
            }
            {(paymentStatus === 'error' && isModal) &&
              <div>
                Si è verificato un errore durante il pagamento. Si prega di riprovare.
              </div>
            }
          </>
          }
        </div>
      }
    </>
  );
};
